import { addDays, type DateKey } from '@arrow/game-engine';
import { useEffect, useState } from 'react';

import { type Archive, ApiError, fetchArchive, fetchPractice, type LoadError, type PracticeSummary } from '../../api/client.ts';

/**
 * Play's data, loaded once a visit and kept for the session: the whole archive (the api
 * lists 92 days a request, so older ranges are fetched until No. 1 is in) and the
 * practice pool. A game opened from Play reads the same copy for its "next in stack", and
 * "‹ Play" finds it ready, so the list is back at once, where you left it.
 */

const FRESH_MS = 5 * 60_000;
/** A runaway guard: ~10 years of dailies. */
const MAX_CHUNKS = 40;
const SPAN = 92;

interface Cached<T> {
	key: string;
	at: number;
	value: T;
}

let archiveCache: Cached<Archive> | null = null;
let archiveInflight: { key: string; promise: Promise<Archive> } | null = null;
let practiceCache: Cached<PracticeSummary[]> | null = null;
let practiceInflight: Promise<PracticeSummary[]> | null = null;

const fresh = <T,>(c: Cached<T> | null, key: string): c is Cached<T> => !!c && c.key === key && Date.now() - c.at < FRESH_MS;

/** What's already loaded for today, if anything (for the first render). */
export function peekArchive(todayKey: DateKey): Archive | null {
	return archiveCache && archiveCache.key === todayKey ? archiveCache.value : null;
}

export function peekPractice(): PracticeSummary[] | null {
	return practiceCache?.value ?? null;
}

async function fetchWhole(): Promise<Archive> {
	const first = await fetchArchive();
	let days = first.days;
	const earliest = first.earliest;
	for (let i = 0; i < MAX_CHUNKS && earliest && days[0] && days[0].date > earliest.date; i++) {
		const to = addDays(days[0].date, -1);
		const from = addDays(to, -(SPAN - 1));
		const chunk = await fetchArchive({ from: from < earliest.date ? earliest.date : from, to });
		if (chunk.days.length === 0) break;
		days = [...chunk.days.filter((d) => d.date < days[0]!.date), ...days];
	}
	return { earliest: first.earliest, latest: first.latest, days };
}

/** The whole archive, from the session's copy when it's fresh. */
export function loadArchive(todayKey: DateKey): Promise<Archive> {
	if (fresh(archiveCache, todayKey)) return Promise.resolve(archiveCache.value);
	if (archiveInflight?.key === todayKey) return archiveInflight.promise;
	const promise = fetchWhole().then(
		(value) => {
			archiveCache = { key: todayKey, at: Date.now(), value };
			archiveInflight = null;
			return value;
		},
		(err: unknown) => {
			archiveInflight = null;
			throw err;
		},
	);
	archiveInflight = { key: todayKey, promise };
	return promise;
}

/** The practice pool (empty, not an error, when the api has none or can't be reached). */
export function loadPractice(): Promise<PracticeSummary[]> {
	if (fresh(practiceCache, 'p')) return Promise.resolve(practiceCache.value);
	practiceInflight ??= fetchPractice().then(
		(value) => {
			practiceCache = { key: 'p', at: Date.now(), value };
			practiceInflight = null;
			return value;
		},
		(err: unknown) => {
			practiceInflight = null;
			if (err instanceof ApiError && err.detail.kind === 'not-found') return [];
			throw err;
		},
	);
	return practiceInflight;
}

/** Tests: forget the session's copy. */
export function resetPlayData(): void {
	archiveCache = null;
	archiveInflight = null;
	practiceCache = null;
	practiceInflight = null;
}

export type PlayData =
	| { status: 'loading' }
	| { status: 'error'; error: LoadError }
	| { status: 'ready'; archive: Archive; practice: PracticeSummary[] };

function toError(err: unknown): LoadError {
	return err instanceof ApiError ? err.detail : { kind: 'network', message: 'Something went wrong.' };
}

/** The archive and the practice pool, for a screen. `attempt` re-asks after an error. */
export function usePlayData(todayKey: DateKey, attempt = 0): PlayData {
	const [data, setData] = useState<PlayData>(() => {
		const a = peekArchive(todayKey);
		return a ? { status: 'ready', archive: a, practice: peekPractice() ?? [] } : { status: 'loading' };
	});
	useEffect(() => {
		let live = true;
		const practice = loadPractice().catch(() => [] as PracticeSummary[]);
		loadArchive(todayKey).then(
			async (archive) => {
				const p = await practice;
				if (live) setData({ status: 'ready', archive, practice: p });
			},
			(err: unknown) => {
				if (live) setData((d) => (d.status === 'ready' ? d : { status: 'error', error: toError(err) }));
			},
		);
		return () => {
			live = false;
		};
	}, [todayKey, attempt]);
	return data;
}
